import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { SubcategoryProcess as View } from '@views';
import { LOADING, SUCCESS, FAILURE } from '@constants/requestPhase';
import { routes } from '@constants';
import { isClientError } from '@helpers/errorHandling';
import { isPriceValid, formatPrice } from '@helpers/itemEdit';
import { formatPrice as formatDiscountAmount } from '@helpers/menuItemDetails';
import { getSubcategoryItems, getSubcategorySizes } from '@helpers/categoryEdit';
import { getEntityAvailabilities, getNumberFromString, hasSizesWithNullablePrice } from '@helpers/common';
import {
  editCategory, deleteCategory, createSubcategory, detachAvailabilityFromCategory, editAvailability, deleteAvailability,
  editSpecialCategory, createSpecialCategory, deleteSpecialCategory, detachAvailabilityFromSpecialCategory,
} from '@store/menu/duck';
import { getSubcategorySpecials } from 'src/helpers/categoryEdit';

const SubcategoryProcess = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const subcategoryId = location.state ? location.state.id : null;

  const {
    items, subcategories, sizes, editCategoryPhase, deleteCategoryPhase,
    createSubcategoryPhase, createSubcategoryError, availabilities,
    detachAvailabilityFromCategoryPhase, detachAvailabilityFromCategoryError,
    deleteAvailabilityPhase, editAvailabilityPhase, specialsCategory, editSpecialCategoryPhase,
    createSpecialCategoryPhase, deleteSpecialCategoryPhase, detachAvailabilityFromSpecialCategoryPhase,
    detachAvailabilityFromSpecialCategoryError,
  } = useSelector(state => state.menu);

  const memoizedItems = useMemo(() => getSubcategoryItems(subcategoryId, items), [subcategoryId]);
  const initialSubcategoryItemsOrder = Object.keys(items).reduce((acc, id) => {
    if (items[id].subcategoryId === subcategoryId) {
      acc.push(parseInt(id, 10));
    }
    return acc;
  }, []);
  const initialActiveState = subcategoryId && subcategories[subcategoryId]
    ? subcategories[subcategoryId].active
    : true;
  const memoizedSpecials = useMemo(() => (
    getSubcategorySpecials(specialsCategory, subcategories, subcategoryId)
  ), [specialsCategory, subcategories, subcategoryId]);
  const memoizedAvailabilities = useMemo(() => (
    getEntityAvailabilities(subcategories[subcategoryId]?.availabilityIds, availabilities)
  ), [availabilities, subcategories]);

  const [subcategoryItems, setSubcategoryItems] = useState(memoizedItems);
  const [isSubcategoryActive, setIsSubcategoryActive] = useState(initialActiveState);
  const [subcategoryName, setSubcategoryName] = useState(subcategoryId ? subcategories[subcategoryId].name : '');
  const [hasServerError, setHasServerError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState('');
  const itemsOrder = subcategoryItems.map(subcategory => subcategory.id);

  // Check if subcateg. name !== '', and if something has changed (we don't need to edit subcateg. if info is the same)
  const isConfirmButtonDisabled = !subcategoryName;

  const isDataTheSame = (!!subcategoryId && subcategoryName === subcategories[subcategoryId].name)
    && (isSubcategoryActive === subcategories[subcategoryId].active)
    && (JSON.stringify(itemsOrder) === JSON.stringify(initialSubcategoryItemsOrder));

  // <-------- Subcategory availability --------->//

  const [availabilityList, setAvailabilityList] = useState(memoizedAvailabilities);
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);
  const [availabilityId, setAvailabilityId] = useState(null);
  const [availabilityName, setAvailabilityName] = useState('');
  const [selectedWeekDays, setSelectedWeekDays] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isAvailabilityDeleteModalOpen, setIsAvailabilityDeleteModalOpen] = useState(false);

  // Check if something has changed (we don't need to edit availability if info is the same)
  const isAvailabilityDataTheSame = (!!availabilityId && !!availabilities[availabilityId]
    && startDate
    && ((!endDate && !availabilities[availabilityId].endDate) || endDate)
    && startTime
    && endTime
    && (availabilities[availabilityId].name === availabilityName)
    && (availabilities[availabilityId].startDate === startDate.toISOString())
    && availabilities[availabilityId].startTime === startTime.toISOString()
    && ((availabilities[availabilityId].endDate && availabilities[availabilityId].endDate === endDate.toISOString())
    || (!availabilities[availabilityId].endDate && !endDate))
    && availabilities[availabilityId].endTime === endTime.toISOString()
    && JSON.stringify(selectedWeekDays) === JSON.stringify(availabilities[availabilityId].weekDays));

  const isAvailabilityConfirmButtonDisabled = !availabilityName
  || !selectedWeekDays.length
  || !startTime
  || !endTime
  || !startDate
  || new Date(startTime.setDate(1)).toISOString() > new Date(endTime.setDate(1)).toISOString()
  // set date to have the same date and compare only time
  || (!!endDate && new Date(startDate).toISOString() > new Date(endDate).toISOString());

  // <-------- Subcategory specials ---------> //

  const [specials, setSpecials] = useState(memoizedSpecials);
  const [isSizesSpecialModalOpen, setIsSizesSpecialModalOpen] = useState(false);
  const [isSpecialModalOpen, setIsSpecialModalOpen] = useState(false);
  const [discountType, setDiscountType] = useState('price');
  const [price, setPrice] = useState('0');
  const [discountAmount, setDiscountAmount] = useState('0');
  const [discountPercent, setDiscountPercent] = useState('0');
  const [specialId, setSpecialId] = useState(null);
  const [selectedSizeSpecial, setSelectedSizeSpecial] = useState(null);
  const [isSpecialDeleteModalOpen, setIsSpecialDeleteModalOpen] = useState(false);
  const [isSpecialWarningModalOpen, setIsSpecialWarningModalOpen] = useState(false);

  const memoizedSpecialAvailabilities = useMemo(() => (
    getEntityAvailabilities(specialsCategory[specialId]?.availabilityIds, availabilities)
  ), [availabilities, specialsCategory, specialId]);

  const memoizedSubcategorySizes = useMemo(() => (
    getSubcategorySizes(subcategoryId, items, sizes)
  ), [subcategoryId, items, sizes]);

  useEffect(() => {
    if (!location.state) {
      history.goBack();
    }
  }, []);

  const handleSpecialModalChangeState = () => {
    setSpecialId(null);
    setPrice(0);
    setDiscountAmount(0);
    setDiscountPercent(0);
    setDiscountType('price');
    setIsSpecialModalOpen(prevState => !prevState);
  };

  useEffect(() => {
    if (editCategoryPhase === SUCCESS
      || deleteCategoryPhase === SUCCESS) {
      history.push({
        pathname: routes.menuItems,
        state: {
          selectedCategoryId: location.state.parentId,
        },
      });
    }
    if (editCategoryPhase === FAILURE
     || deleteCategoryPhase === FAILURE) {
      setHasServerError(true);
    }
  }, [editCategoryPhase, deleteCategoryPhase]);

  useEffect(() => {
    if (createSubcategoryPhase === SUCCESS) {
      history.push({
        pathname: routes.menuItems,
        state: {
          selectedCategoryId: location.state.parentId,
        },
      });
    }

    if (createSubcategoryPhase === FAILURE) {
      setIsModalOpen(false);
      if (isClientError(createSubcategoryError && createSubcategoryError.data.status)) { // Case when we get 4** error
        setError(createSubcategoryError.data.error);
      } else {
        setHasServerError(true);
      }
    }
  }, [createSubcategoryPhase]);

  useEffect(() => {
    if (detachAvailabilityFromCategoryPhase === SUCCESS) {
      setAvailabilityList(getEntityAvailabilities(subcategories[subcategoryId]?.availabilityIds, availabilities));
    }
    if (detachAvailabilityFromCategoryPhase === FAILURE) {
      if (isClientError(detachAvailabilityFromCategoryError && detachAvailabilityFromCategoryError.data.status)) {
        setError(detachAvailabilityFromCategoryError.data.error);
      }
    }
  }, [detachAvailabilityFromCategoryPhase]);

  useEffect(() => {
    if (detachAvailabilityFromSpecialCategoryPhase === FAILURE) {
      if (isClientError(detachAvailabilityFromSpecialCategoryError
         && detachAvailabilityFromSpecialCategoryError.data.status)) {
        setError(detachAvailabilityFromSpecialCategoryError.data.error);
      }
    }
  }, [detachAvailabilityFromSpecialCategoryPhase]);

  useEffect(() => {
    if (editAvailabilityPhase === SUCCESS
      || deleteAvailabilityPhase === SUCCESS) {
      setAvailabilityList(getEntityAvailabilities(subcategories[subcategoryId]?.availabilityIds, availabilities));
      setIsAvailabilityDeleteModalOpen(false);
      setIsAvailabilityModalOpen(false);
    }
    if (editAvailabilityPhase === FAILURE
      || deleteAvailabilityPhase === FAILURE) {
      setIsAvailabilityModalOpen(false);
    }
  }, [deleteAvailabilityPhase, editAvailabilityPhase]);

  useEffect(() => {
    if (editSpecialCategoryPhase === SUCCESS
      || createSpecialCategoryPhase === SUCCESS
      || deleteSpecialCategoryPhase === SUCCESS) {
      setSpecials(getSubcategorySpecials(specialsCategory, subcategories, subcategoryId));
      handleSpecialModalChangeState();
      setIsSpecialDeleteModalOpen(false);
      setIsSpecialWarningModalOpen(false);
    }
    if (editSpecialCategoryPhase === FAILURE
      || createSpecialCategoryPhase === FAILURE
      || deleteSpecialCategoryPhase === FAILURE) {
      handleSpecialModalChangeState();
      setIsSpecialDeleteModalOpen(false);
      setIsSpecialWarningModalOpen(false);
    }
  }, [editSpecialCategoryPhase, createSpecialCategoryPhase, deleteSpecialCategoryPhase]);

  // <-------- Subcategory actions --------->//

  const handleToastClose = () => {
    setHasServerError(false);
  };

  const handleSubcategoryActivityChange = () => {
    setIsSubcategoryActive(prevState => !prevState);
  };

  const handleModalChangeState = () => {
    setIsModalOpen(prevState => !prevState);
  };

  const handleEditCancel = () => {
    history.push({
      pathname: routes.menuItems,
      state: {
        selectedCategoryId: location.state.parentId,
      },
    });
  };

  const handleSubcategoryNameChange = e => {
    setSubcategoryName(e.target.value);
  };

  const handleEditSubmit = () => {
    if (isDataTheSame) {
      handleEditCancel();
    } else {
      const subcategoryData = new FormData();

      if (!subcategoryId || (subcategoryId && subcategoryName !== subcategories[subcategoryId].name)) {
        subcategoryData.append('name', subcategoryName);
      }

      if (subcategoryId && JSON.stringify(itemsOrder) !== JSON.stringify(initialSubcategoryItemsOrder)) {
        subcategoryData.append('itemsOrder', JSON.stringify(itemsOrder));
      }

      if (!subcategoryId || (subcategoryId && isSubcategoryActive !== subcategories[subcategoryId].active)) {
        subcategoryData.append('active', isSubcategoryActive);
      }

      if (subcategoryId) {
        subcategoryData.append('id', subcategoryId);

        dispatch(editCategory(
          subcategoryData,
        ));
      } else {
        dispatch(createSubcategory({
          name: subcategoryName,
          active: isSubcategoryActive,
          parentId: parseInt(location.state.parentId, 10),
        }));
      }
    }
  };

  const handleSubcategoryDelete = () => {
    dispatch(deleteCategory({
      id: parseInt(subcategoryId, 10),
    }));
  };

  const handleDragEnd = ({ destination, source }) => {
    if (destination && destination.index !== source.index) {
      const reorderedItems = [...subcategoryItems];
      reorderedItems.splice(source.index, 1);
      reorderedItems.splice(destination.index, 0, subcategoryItems[source.index]);
      setSubcategoryItems(reorderedItems);
    }
  };

  // <-------- Subcategory special actions ---------> //

  const handleDiscountTypeChange = typeId => () => {
    setDiscountType(typeId);
  };

  const handlePriceChange = e => {
    const cash = e.target.value.replace(',', '.').replace('$', '');
    if (isPriceValid(cash)) { // we check if entered price !== 0 to not replace whole value with empty string
      setPrice(formatPrice(parseInt(cash, 10) !== 0 && parseInt(price, 10) === 0
        ? cash.replace('0', '')
        : cash));
    }
  };

  const handleDiscountAmountChange = e => {
    const cash = e.target.value.replace(',', '.').replace('$', '');
    if (isPriceValid(cash)) { // we check if entered value !== 0 to not replace whole value with empty string
      setDiscountAmount(formatPrice(parseInt(cash, 10) !== 0 && parseInt(discountAmount, 10) === 0
        ? cash.replace('0', '')
        : cash));
    }
  };

  const handleDiscountPercentChange = e => {
    let cash;
    if (!e.target.value.includes('%')) {
      cash = e.target.value.replace(',', '.').slice(0, -1);
    } else {
      cash = e.target.value.replace(',', '.').replace('%', '');
    }
    if (isPriceValid(cash)) { // we check if entered value !== 0 to not replace whole value with empty string
      setDiscountPercent(formatPrice(parseInt(cash, 10) !== 0 && parseInt(discountPercent, 10) === 0
        ? cash.replace('0', '')
        : cash));
    }
  };

  const handleSelectedSizeSpecialChange = sizeId => () => {
    setSelectedSizeSpecial(sizeId);
    setIsSizesSpecialModalOpen(false);
    setIsSpecialModalOpen(true);
  };

  const setDiscountValue = id => {
    if (specialsCategory[id].discountType === 'price') {
      setPrice(formatDiscountAmount(specialsCategory[id].discountValue));
    }
    if (specialsCategory[id].discountType === 'discount_amount') {
      setDiscountAmount(formatDiscountAmount(specialsCategory[id].discountValue));
    }
    if (specialsCategory[id].discountType === 'discount_percent') {
      setDiscountPercent(formatDiscountAmount(specialsCategory[id].discountValue));
    }
  };

  const handleSpecialEdit = id => () => {
    setSpecialId(id);
    setSelectedSizeSpecial(memoizedSubcategorySizes.find(size => size.name === specialsCategory[id].sizeName).id);
    setDiscountType(specialsCategory[id].discountType);
    setDiscountValue(id);
    setIsSpecialModalOpen(prevState => !prevState);
  };

  const handleSizesSpecialModalChangeState = () => {
    setIsSizesSpecialModalOpen(prevState => !prevState);
  };

  const handleSpecialAdd = () => {
    setIsSizesSpecialModalOpen(true);
  };

  const discountTypes = {
    price: {
      name: 'PRICE',
      inputValue: `$${price}`,
      inputHandler: handlePriceChange,
    },
    discount_amount: {
      name: 'DISCOUNT AMOUNT',
      inputValue: `$${discountAmount}`,
      inputHandler: handleDiscountAmountChange,
    },
    discount_percent: {
      name: 'DISCOUNT PERCENT',
      inputValue: `${discountPercent}%`,
      inputHandler: handleDiscountPercentChange,
    },
  };

  const handleSpecialDeleteModalChangeState = () => {
    setIsSpecialDeleteModalOpen(prevState => !prevState);
  };

  const handleSpecialDelete = () => {
    dispatch(deleteSpecialCategory({
      id: parseInt(specialId, 10),
    }));
  };

  const isSpecialDataTheSame = (!!specialId && !!specialsCategory[specialId]
    && (specialsCategory[specialId].discountType === discountType)
    && (specialsCategory[specialId].discountValue === getNumberFromString(discountTypes[discountType].inputValue))
  );

  const isSpecialConfirmButtonDisabled = !getNumberFromString(discountTypes[discountType].inputValue);

  const handleEditConfirm = () => {
    const specialCategory = {
      discountType,
      discountValue: getNumberFromString(discountTypes[discountType].inputValue),
    };

    if (specialId) {
      specialCategory.id = specialId;
      dispatch(editSpecialCategory(specialCategory));
    } else {
      specialCategory.categoryId = subcategoryId;
      specialCategory.sizeName = sizes[selectedSizeSpecial].name;
      dispatch(createSpecialCategory(specialCategory));
    }
  };

  const handleSpecialEditSubmit = () => {
    if (isSpecialDataTheSame) {
      handleSpecialModalChangeState();
    } else if (hasSizesWithNullablePrice(memoizedSubcategorySizes, sizes, selectedSizeSpecial,
      discountType, discountTypes[discountType].inputValue)) {
      setIsSpecialWarningModalOpen(true);
    } else {
      handleEditConfirm();
    }
  };

  const handleSpecialWarningModalClose = () => {
    setIsSpecialWarningModalOpen(false);
  };

  const handleSpecialAvailabilityDetach = id => () => {
    dispatch(detachAvailabilityFromSpecialCategory({
      availabilityId: parseInt(id, 10),
      specialCategoryId: parseInt(specialId, 10),
    }));
  };

  const handleSpecialAvailabilityAdd = () => {
    history.push({
      pathname: routes.availabilitiesList,
      state: {
        id: specialId,
        appliedIds: specialsCategory[specialId]?.availabilityIds,
        entity: 'specialCategory',
      },
    });
  };

  // <-------- Subcategory availability actions --------->//

  const handleAvailabilityDetach = id => () => {
    dispatch(detachAvailabilityFromCategory({
      availabilityId: parseInt(id, 10),
      categoryId: parseInt(subcategoryId, 10),
    }));
  };

  const handleAvailabilityAdd = () => {
    history.push({
      pathname: routes.availabilitiesList,
      state: {
        id: subcategoryId,
        entity: 'category',
        appliedIds: subcategories[subcategoryId]?.availabilityIds,
      },
    });
  };

  const handleDayClick = dayId => () => {
    const days = [...selectedWeekDays];
    if (days.includes(dayId)) {
      const index = days.indexOf(dayId);

      if (index > -1) {
        days.splice(index, 1);
      }
    } else {
      days.push(dayId);
    }
    setSelectedWeekDays(days);
  };

  const handleAvailabilityDeleteModalChangeState = () => {
    setIsAvailabilityDeleteModalOpen(prevState => !prevState);
  };

  const handleAvailabilityNameChange = e => {
    setAvailabilityName(e.target.value);
  };

  const handleAvailabilityModalChangeState = () => {
    setAvailabilityId(null);
    setAvailabilityName('');
    setStartTime('');
    setEndTime('');
    setStartDate('');
    setEndDate('');
    setSelectedWeekDays([]);
    setIsAvailabilityModalOpen(prevState => !prevState);
  };

  const handleStartTimeChange = time => {
    setStartTime(time);
  };

  const handleEndTimeChange = time => {
    setEndTime(time);
  };

  const handleStartDateChange = date => {
    setStartDate(date);
  };

  const handleEndDateChange = date => {
    setEndDate(date);
  };

  const handleAvailabilityEdit = editingId => () => {
    setIsAvailabilityModalOpen(true);
    setAvailabilityId(editingId);
    setAvailabilityName(availabilities[editingId].name);
    setStartTime(new Date(availabilities[editingId].startTime));
    setEndTime(new Date(availabilities[editingId].endTime));
    setStartDate(new Date(availabilities[editingId].startDate));
    setEndDate(availabilities[editingId].endDate ? new Date(availabilities[editingId].endDate) : '');
    setSelectedWeekDays(availabilities[editingId].weekDays);
  };

  const handleAvailabilityDelete = () => {
    dispatch(deleteAvailability({
      id: parseInt(availabilityId, 10),
    }));
  };

  const handleAvailabilityEditSubmit = () => {
    if (isAvailabilityDataTheSame) {
      setIsAvailabilityModalOpen(false);
    } else {
      const availability = {};
      if ((availabilityName && !availabilityId)
        || (availabilityName && availabilityId && availabilityName !== availabilities[availabilityId].name)) {
        availability.name = availabilityName;
      }
      if ((selectedWeekDays.length && !availabilityId)
        || (selectedWeekDays.length && availabilityId
          && JSON.stringify(selectedWeekDays) !== JSON.stringify(availabilities[availabilityId].weekDays))) {
        availability.weekDays = selectedWeekDays;
      }
      if (startDate) {
        availability.startDate = startDate.toISOString();
      }
      if (endDate) {
        availability.endDate = endDate.toISOString();
      }
      if (!endDate && !!availabilities[availabilityId]?.endDate) {
        availability.endDate = null;
      }
      if (startTime) {
        availability.startTime = startTime.toISOString();
      }
      if (endTime) {
        availability.endTime = endTime.toISOString();
      }
      availability.id = parseInt(availabilityId, 10);

      dispatch(editAvailability(availability));
    }
  };

  const handleStartDateRemove = () => {
    setStartDate('');
  };

  const handleEndDateRemove = () => {
    setEndDate('');
  };

  const handleStartTimeRemove = () => {
    setStartTime('');
  };

  const handleEndTimeRemove = () => {
    setEndTime('');
  };

  return (
    <View
      // special
      onSpecialWarningModalClose={handleSpecialWarningModalClose}
      onEditConfirm={handleEditConfirm}
      isSpecialWarningModalOpen={isSpecialWarningModalOpen}
      onSpecialAvailabilityAdd={handleSpecialAvailabilityAdd}
      specialAvailabilities={memoizedSpecialAvailabilities}
      onSpecialAvailabilityDetach={handleSpecialAvailabilityDetach}
      isSpecialConfirmButtonDisabled={isSpecialConfirmButtonDisabled}
      onSpecialDeleteModalChangeState={handleSpecialDeleteModalChangeState}
      onSpecialDelete={handleSpecialDelete}
      isSpecialDeleteModalOpen={isSpecialDeleteModalOpen}
      onSpecialEditSubmit={handleSpecialEditSubmit}
      subcategorySizes={memoizedSubcategorySizes}
      selectedSizeSpecial={selectedSizeSpecial}
      onSpecialAdd={handleSpecialAdd}
      onSelectedSizeSpecialChange={handleSelectedSizeSpecialChange}
      onSizesSpecialModalChangeState={handleSizesSpecialModalChangeState}
      isSizesSpecialModalOpen={isSizesSpecialModalOpen}
      onSpecialEdit={handleSpecialEdit}
      isEditingSpecial={!!specialId}
      discountTypes={discountTypes}
      onSpecialModalChangeState={handleSpecialModalChangeState}
      isSpecialModalOpen={isSpecialModalOpen}
      onDiscountTypeChange={handleDiscountTypeChange}
      selectedDiscountType={discountType}
      subcategorySpecials={specials}
      // subcategory availability
      onStartDateRemove={handleStartDateRemove}
      onEndDateRemove={handleEndDateRemove}
      onStartTimeRemove={handleStartTimeRemove}
      onEndTimeRemove={handleEndTimeRemove}
      onAvailabilityEdit={handleAvailabilityEdit}
      isAvailabilityConfirmButtonDisabled={isAvailabilityConfirmButtonDisabled}
      onAvailabilityModalChangeState={handleAvailabilityModalChangeState}
      isAvailabilityModalOpen={isAvailabilityModalOpen}
      onAvailabilityDelete={handleAvailabilityDelete}
      onAvailabilityDeleteModalChangeState={handleAvailabilityDeleteModalChangeState}
      onAvailabilityEditSubmit={handleAvailabilityEditSubmit}
      isAvailabilityDeleteModalOpen={isAvailabilityDeleteModalOpen}
      isAvailabilityEditing={!!availabilityId}
      startTime={startTime.toString()}
      endTime={endTime.toString()}
      startDate={startDate.toString()}
      endDate={endDate.toString()}
      onStartTimeChange={handleStartTimeChange}
      onEndTimeChange={handleEndTimeChange}
      onStartDateChange={handleStartDateChange}
      onEndDateChange={handleEndDateChange}
      onDayClick={handleDayClick}
      selectedWeekDays={selectedWeekDays}
      onAvailabilityNameChange={handleAvailabilityNameChange}
      availabilityName={availabilityName}
      onAvailabilityDetach={handleAvailabilityDetach}
      availabilities={availabilityList}
      onAvailabilityAdd={handleAvailabilityAdd}
      // subcategory
      isSubcategoryActive={isSubcategoryActive}
      onSubcategoryActivityChange={handleSubcategoryActivityChange}
      onModalChangeState={handleModalChangeState}
      isModalOpen={isModalOpen}
      isEditing={!!subcategoryId}
      onSubcategoryDelete={handleSubcategoryDelete}
      isConfirmButtonDisabled={isConfirmButtonDisabled}
      onDragEnd={handleDragEnd}
      onEditCancel={handleEditCancel}
      items={subcategoryItems}
      subcategoryName={subcategoryName}
      onSubcategoryNameChange={handleSubcategoryNameChange}
      onEditSubmit={handleEditSubmit}
      onToastClose={handleToastClose}
      hasServerError={hasServerError}
      error={error}
      isLoading={LOADING === editCategoryPhase
        || LOADING === deleteCategoryPhase
        || LOADING === createSubcategoryPhase
        || LOADING === detachAvailabilityFromCategoryPhase
        || deleteAvailabilityPhase === LOADING
        || editAvailabilityPhase === LOADING
        || editSpecialCategoryPhase === LOADING
        || createSpecialCategoryPhase === LOADING
        || deleteSpecialCategoryPhase === LOADING
        || detachAvailabilityFromSpecialCategoryPhase === LOADING}
    />
  );
};

export default SubcategoryProcess;
